export type Scalar = string | number | boolean;
export type NumOp = '>' | '>=' | '<' | '<=';
export type Field<T> = Extract<keyof T, string>;
export type Path<T> = readonly [Field<T>, ...string[]];

export type ElemPredicate =
  | { readonly kind: 'eq'; readonly field: string; readonly value: Scalar }
  | { readonly kind: 'cmp'; readonly field: string; readonly op: NumOp; readonly value: number }
  | { readonly kind: 'and'; readonly operands: readonly ElemPredicate[] };

export type Predicate<T> =
  | { kind: 'always' }
  | { kind: 'never' }
  | { kind: 'eq'; field: Field<T>; value: Scalar }
  | { kind: 'eqStringLiteral'; field: Field<T>; value: string }
  | { kind: 'neq'; field: Field<T>; value: Scalar }
  | { kind: 'in'; field: Field<T>; values: readonly Scalar[] }
  | { kind: 'cmp'; field: Field<T>; op: NumOp; value: number }
  | { kind: 'pathCmp'; path: Path<T>; op: NumOp; value: number }
  | { kind: 'and'; operands: readonly Predicate<T>[] }
  | { kind: 'or'; operands: readonly Predicate<T>[] }
  | { kind: 'not'; operand: Predicate<T> }
  | { readonly kind: 'elemMatch'; readonly field: Field<T>; readonly where: ElemPredicate };

export const always = <T>(): Predicate<T> => ({ kind: 'always' });
export const never = <T>(): Predicate<T> => ({ kind: 'never' });
export const eq = <T>(field: Field<T>, value: Scalar): Predicate<T> => ({ kind: 'eq', field, value });
/** An explicit string literal; each provider chooses its own delimiter syntax. */
export const eqStringLiteral = <T>(field: Field<T>, value: string): Predicate<T> => ({
  kind: 'eqStringLiteral',
  field,
  value,
});
export const neq = <T>(field: Field<T>, value: Scalar): Predicate<T> => ({ kind: 'neq', field, value });
export const cmp = <T>(field: Field<T>, op: NumOp, value: number): Predicate<T> => ({ kind: 'cmp', field, op, value });
export const pathCmp = <T>(path: Path<T>, op: NumOp, value: number): Extract<Predicate<T>, { kind: 'pathCmp' }> => ({
  kind: 'pathCmp',
  path,
  op,
  value,
});
/** One array element must satisfy every clause of `where` at once. */
export const elemMatch = <T>(field: Field<T>, where: ElemPredicate): Predicate<T> => ({
  kind: 'elemMatch',
  field,
  where,
});
export const not = <T>(operand: Predicate<T>): Predicate<T> => {
  switch (operand.kind) {
    case 'always':
      return never();
    case 'never':
      return always();
    case 'neq':
      return eq(operand.field, operand.value);
    case 'not':
      return operand.operand;
    default:
      return { kind: 'not', operand };
  }
};

export const inList = <T>(field: Field<T>, values: readonly Scalar[]): Predicate<T> => {
  if (values.length === 0) return never();

  const [onlyValue, ...remainingValues] = values;
  if (remainingValues.length === 0) {
    return onlyValue === undefined ? never() : eq(field, onlyValue);
  }

  return { kind: 'in', field, values };
};

/** Explicit choice for optional values: absent means "match nothing", never "don't constrain". */
export const eqOpt = <T>(field: Field<T>, value: Scalar | null | undefined): Predicate<T> =>
  value === null || value === undefined ? never() : eq(field, value);

const fold = <T>(kind: 'and' | 'or', operands: readonly Predicate<T>[]): Predicate<T> => {
  const absorbing = kind === 'and' ? 'never' : 'always';
  const identity = kind === 'and' ? 'always' : 'never';

  if (operands.some((operand) => operand.kind === absorbing)) {
    return absorbing === 'never' ? never() : always();
  }

  const kept = operands.filter((operand) => operand.kind !== identity);
  if (kept.length === 0) return identity === 'always' ? always() : never();

  const [onlyKept, ...remainingKept] = kept;
  if (remainingKept.length === 0) {
    return onlyKept === undefined ? (identity === 'always' ? always() : never()) : onlyKept;
  }

  return { kind, operands: kept };
};

export const and = <T>(operands: readonly Predicate<T>[]): Predicate<T> => fold('and', operands);
export const or = <T>(operands: readonly Predicate<T>[]): Predicate<T> => fold('or', operands);
